<section class="contracts-page" aria-label="Contratos">
	<div class="contracts-state" *ngIf="isLoading" aria-live="polite">
		<mat-spinner diameter="24"></mat-spinner>
		<span>Carregando contratos...</span>
	</div>

	<div
		class="contracts-state contracts-state--empty"
		*ngIf="isEmpty && !isCreating"
	>
		<div class="contracts-state__message">
			<mat-icon class="contracts-state__icon">description</mat-icon>
			<div>
				<strong>Esta organização ainda não possui contrato.</strong>
				<p>Crie manualmente ou importe um documento para análise.</p>
			</div>
		</div>
		<app-contract-attachment-dropzone
			[acceptedExtensions]="acceptedAttachmentExtensions"
			[isDisabled]="isImportingNew"
			[isProcessing]="isImportingNew"
			(fileSelected)="importNewAttachment($event)"
			(selectionError)="showAttachmentSelectionError($event)"
		></app-contract-attachment-dropzone>
		<div class="contracts-state__actions">
			<button
				mat-flat-button
				color="primary"
				type="button"
				(click)="startCreating()"
			>
				<mat-icon svgIcon="sanus-add-blue"></mat-icon>
				Criar manualmente
			</button>
		</div>
	</div>

	<article class="contract-card" *ngIf="isEmpty && isCreating">
		<header class="contract-card__header">
			<div class="contract-card__title">
				<mat-icon class="contract-card__title-icon">note_add</mat-icon>
				<div>
					<h2>Novo contrato</h2>
					<p class="contract-card__name">
						Revise o conteúdo antes de criar na Antonnia.
					</p>
				</div>
			</div>
			<div
				class="contract-view-switch"
				role="tablist"
				aria-label="Visualização do novo contrato"
			>
				<button
					type="button"
					class="contract-view-switch__button"
					[class.contract-view-switch__button--active]="viewMode === 'guided'"
					[attr.aria-pressed]="viewMode === 'guided'"
					(click)="setViewMode('guided')"
				>
					Normal
				</button>
				<button
					type="button"
					class="contract-view-switch__button"
					[class.contract-view-switch__button--active]="viewMode === 'advanced'"
					[attr.aria-pressed]="viewMode === 'advanced'"
					(click)="setViewMode('advanced')"
				>
					Avançada
				</button>
			</div>
		</header>

		<div class="contract-card__editor">
			<div class="contract-card__pending-attachment" *ngIf="pendingAttachment">
				<mat-icon class="contract-card__pending-icon">attach_file</mat-icon>
				<div>
					<strong>{{ pendingAttachment.name }}</strong>
					<span>O anexo será armazenado quando você criar o contrato.</span>
				</div>
			</div>

			<app-contract-section-editor
				*ngIf="viewMode === 'guided'"
				[editable]="!isSaving"
				[value]="contractContentControl.value"
				(valueChange)="contractContentControl.setValue($event)"
			></app-contract-section-editor>

			<app-contract-code-editor
				*ngIf="viewMode === 'advanced'"
				[editable]="!isSaving"
				[value]="contractContentControl.value"
				(valueChange)="contractContentControl.setValue($event)"
			></app-contract-code-editor>

			<div class="contract-card__editor-actions">
				<button
					mat-button
					type="button"
					[disabled]="isSaving"
					(click)="cancelCreating()"
				>
					Cancelar
				</button>
				<button
					mat-flat-button
					color="primary"
					type="button"
					[disabled]="isSaving"
					(click)="saveNewContract()"
				>
					{{ isSaving ? 'Criando...' : 'Criar na Antonnia' }}
				</button>
			</div>
		</div>
	</article>

	<div
		class="contracts-state"
		[class.contracts-state--authentication]="isAuthenticationRequired"
		[class.contracts-state--error]="!isAuthenticationRequired"
		*ngIf="isError"
		[attr.role]="isAuthenticationRequired ? null : 'alert'"
	>
		<div class="contracts-state__message">
			<mat-icon class="contracts-state__icon">
				{{ isAuthenticationRequired ? 'info_outline' : 'error_outline' }}
			</mat-icon>
			<p>{{ errorMessage }}</p>
		</div>
		<button
			mat-flat-button
			color="primary"
			type="button"
			*ngIf="isAuthenticationRequired"
			[disabled]="isAuthenticating"
			(click)="authenticate()"
		>
			{{ isAuthenticating ? 'Conectando...' : 'Conectar à Antonnia' }}
		</button>
	</div>

	<div class="contracts-list" *ngIf="isSuccess">
		<div class="contracts-view-toolbar">
			<ng-container *ngIf="contracts[0] as contract">
				<div
					class="contract-card__metadata"
					*ngIf="contract.updatedAt || (contract.attachments?.length ?? 0) > 0"
				>
					<p class="contract-card__updated-at" *ngIf="contract.updatedAt">
						Última alteração
						<ng-container *ngIf="contract.updatedBy">
							por
							<strong>{{ contract.updatedBy.name }}</strong>
						</ng-container>
						em {{ contract.updatedAt | date: 'dd/MM/yyyy HH:mm' }}
					</p>

					<div
						class="contract-card__attachments"
						*ngIf="(contract.attachments?.length ?? 0) > 0"
					>
						<span class="contract-card__attachments-label">Anexos salvos:</span>
						<button
							mat-stroked-button
							type="button"
							class="contract-card__attachment"
							*ngFor="let attachment of contract.attachments ?? []"
							[disabled]="isDownloadingAttachmentId !== null"
							[matTooltip]="
								'Enviado por ' +
								attachment.createdBy.name +
								' em ' +
								(attachment.createdAt | date: 'dd/MM/yyyy HH:mm')
							"
							(click)="downloadAttachment(contract, attachment)"
						>
							<mat-icon svgIcon="sanus-download"></mat-icon>
							{{ attachment.name }} ({{ formatFileSize(attachment.sizeBytes) }})
						</button>
					</div>
				</div>
			</ng-container>

			<div
				class="contract-view-switch"
				role="tablist"
				aria-label="Visualização do contrato"
			>
				<button
					type="button"
					class="contract-view-switch__button"
					[class.contract-view-switch__button--active]="viewMode === 'guided'"
					[attr.aria-pressed]="viewMode === 'guided'"
					(click)="setViewMode('guided')"
				>
					Normal
				</button>
				<button
					type="button"
					class="contract-view-switch__button"
					[class.contract-view-switch__button--active]="viewMode === 'advanced'"
					[attr.aria-pressed]="viewMode === 'advanced'"
					(click)="setViewMode('advanced')"
				>
					Avançada
				</button>
			</div>
		</div>

		<article class="contract-card" *ngFor="let contract of contracts">
			<header class="contract-card__header">
				<div class="contract-card__title">
					<mat-icon class="contract-card__title-icon">description</mat-icon>
					<div>
						<h2>{{ contract.displayName || contract.name }}</h2>
						<p class="contract-card__name" *ngIf="contract.displayName">
							{{ contract.name }}
						</p>
					</div>
				</div>
				<div class="contract-card__header-actions">
					<span class="contract-card__branch" *ngIf="contract.branchName">
						{{ contract.branchName }}
					</span>
					<button
						mat-stroked-button
						type="button"
						class="contract-card__history"
						*ngIf="!isEditing(contract)"
						(click)="openHistory(contract)"
					>
						<mat-icon>history</mat-icon>
						Histórico de alterações
					</button>
					<button
						mat-stroked-button
						color="primary"
						type="button"
						class="contract-card__edit"
						*ngIf="!isEditing(contract)"
						[disabled]="isImportingContractId !== null"
						(click)="startEditing(contract)"
					>
						<mat-icon svgIcon="sanus-edit-blue"></mat-icon>
						Editar
					</button>
				</div>
			</header>

			<app-contract-attachment-dropzone
				*ngIf="!isEditing(contract)"
				class="contract-card__dropzone"
				[acceptedExtensions]="acceptedAttachmentExtensions"
				[isDisabled]="isImportingContractId !== null"
				[isProcessing]="isImporting(contract)"
				(fileSelected)="importAttachment(contract, $event)"
				(selectionError)="showAttachmentSelectionError($event)"
			></app-contract-attachment-dropzone>

			<app-contract-section-editor
				class="contract-card__content"
				*ngIf="
					contract.content && !isEditing(contract) && viewMode === 'guided'
				"
				[value]="contract.content"
			></app-contract-section-editor>

			<app-contract-code-editor
				class="contract-card__content"
				*ngIf="
					contract.content && !isEditing(contract) && viewMode === 'advanced'
				"
				[value]="contract.content"
			></app-contract-code-editor>

			<div class="contract-card__editor" *ngIf="isEditing(contract)">
				<div
					class="contract-card__pending-attachment"
					*ngIf="pendingAttachment"
				>
					<mat-icon class="contract-card__pending-icon">attach_file</mat-icon>
					<div>
						<strong>{{ pendingAttachment.name }}</strong>
						<span>O anexo será armazenado quando você salvar.</span>
					</div>
				</div>
				<app-contract-section-editor
					*ngIf="viewMode === 'guided'"
					[editable]="!isSaving"
					[value]="contractContentControl.value"
					(valueChange)="contractContentControl.setValue($event)"
				></app-contract-section-editor>

				<app-contract-code-editor
					*ngIf="viewMode === 'advanced'"
					[editable]="!isSaving"
					[value]="contractContentControl.value"
					(valueChange)="contractContentControl.setValue($event)"
				></app-contract-code-editor>

				<div class="contract-card__editor-actions">
					<button
						mat-button
						type="button"
						[disabled]="isSaving"
						(click)="cancelEditing()"
					>
						Cancelar
					</button>
					<button
						mat-flat-button
						color="primary"
						type="button"
						class="contract-card__save"
						[disabled]="isSaving"
						(click)="saveContract(contract)"
					>
						{{ isSaving ? 'Salvando...' : 'Salvar na Antonnia' }}
					</button>
				</div>
			</div>
		</article>
	</div>
</section>
